import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Mail, Phone } from "lucide-react";
import { BRAND, CONTACT, OFFICES, NAV_LINKS } from "@/data/site";

export default function Footer() {
  return (
    <footer className="relative bg-navy text-white overflow-hidden" data-testid="footer">
      <div className="absolute inset-0 hero-grid-overlay opacity-30 pointer-events-none" />
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-emerald-brand/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />

      <div className="relative container-x pt-24 pb-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 pb-16 border-b border-white/10">
          <div className="lg:col-span-5">
            <div className="overline text-gold">Let&apos;s build the future</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.95] tracking-tight mt-4">
              Engineering<br />
              <span className="text-gold">tomorrow,</span><br />
              together.
            </h2>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Partner with NEPHRC for infrastructure, hospitality and retail consultancy that delivers measurable outcomes.
            </p>
            <Link
              to="/contact"
              data-testid="footer-start-project"
              className="mt-8 inline-flex items-center gap-2 px-6 py-4 bg-gold text-navy font-semibold text-xs tracking-[0.2em] uppercase hover:bg-[#E8C45F] transition-colors"
            >
              Start a Project <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="lg:col-span-3">
            <div className="overline text-white/50 mb-5">Navigate</div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-white/80 hover:text-gold transition-colors text-sm"
                    data-testid={`footer-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="overline text-white/50 mb-5">Reach Us</div>
            <ul className="space-y-3 text-sm text-white/80">
              {CONTACT.phones.map((p) => (
                <li key={p} className="flex items-center gap-3">
                  <Phone size={14} className="text-gold" />
                  <a href={`tel:${p.replace(/\s/g, "")}`} className="hover:text-white">{p}</a>
                </li>
              ))}
              <li className="flex items-center gap-3">
                <Mail size={14} className="text-gold" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-white">{CONTACT.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8 py-14 border-b border-white/10">
          {OFFICES.map((o) => (
            <div key={o.name} className="space-y-2">
              <div className="flex items-center gap-2 text-gold">
                <MapPin size={14} />
                <span className="overline text-white">{o.city}</span>
              </div>
              <div className="font-display font-semibold text-sm">{o.name}</div>
              <p className="text-xs text-white/60 leading-relaxed">{o.address}</p>
            </div>
          ))}
        </div>

        <div className="pt-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-white/50">
          <div>
            © {new Date().getFullYear()} {BRAND.name}. {BRAND.full}. All rights reserved.
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span>Founded by {BRAND.founder}</span>
            <span className="text-white/30 hidden md:inline">|</span>
            <a
              href="https://www.thegrowinggraph.com/"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-credit-tgg"
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-gold transition-colors"
            >
              Designed by <span className="font-display font-semibold text-white/85 hover:text-gold">The Growing Graph</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
